import { useState, useEffect, useCallback } from 'react';
import { Card, Form, Row, Col, Select, Input, Button, Tag } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSearchParams } from 'react-router-dom';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useAppSelector } from '../../methods/redux/hooks';
import DateRangePickerUTC from './DateRangePickerUTC';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

interface Filters {
	startTime: string;
	endTime: string;
	network: string;
	currency: string;
	walletAddress: string;
}

interface ISearchFilter {
	retrieveQueryString: (
		params: Record<string, string>,
		_action?: string
	) => void;
}

interface SelectOption {
	name: string;
	value: string;
}

const searchFilters = [
	{ name: 'network', label: 'Blockchain' },
	{ name: 'currency', label: 'Currency' },
	{ name: 'walletAddress', label: 'Wallet Address' },
	{ name: 'startTime', label: 'Date Range' },
	{ name: 'endTime', label: 'Date Range' },
];

const FilterRemittanceLogs = ({ retrieveQueryString }: ISearchFilter) => {
	const [filters, setFilters] = useState<Filters>({
		startTime: dayjs.utc().startOf('day').valueOf().toString(),
		endTime: dayjs.utc().endOf('day').valueOf().toString(),
		network: '',
		currency: '',
		walletAddress: '',
	});
	const [dateRange, setDateRange] = useState('today');
	const [startTime, setStartTime] = useState<string | null>(null);
	const [endTime, setEndTime] = useState<string | null>(null);
	const [activeFilters, setActiveFilters] = useState<string[]>(['Date Range']);
	const [searchParams, setSearchParams] = useSearchParams();

	const [dateRangeError, setDateRangeError] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const { currencies, blockchains } = useAppSelector(
		(store) => store.currencies
	);

	const currencySelect: SelectOption[] = currencies.map((item: any) => ({
		name: item.symbol,
		value: String(item.switchWalletId), // Ensure string
	}));

	const blockchainSelect: SelectOption[] = blockchains.map((item: any) => ({
		name: item.abbreviation,
		value: String(item.switchWalletEnum), // Ensure string
	}));

	const getDateRangeFromStartEnd = (startStr: string, endStr: string) => {
		if (
			!startStr ||
			!endStr ||
			Number.isNaN(Number(startStr)) ||
			Number.isNaN(Number(endStr))
		) {
			return 'all';
		}

		const startDayUtc = dayjs.utc(Number(startStr)).startOf('day');
		const endDayUtc = dayjs.utc(Number(endStr)).startOf('day');

		const todayStartUtc = dayjs.utc().startOf('day');

		if (
			startDayUtc.isSame(todayStartUtc, 'day') &&
			endDayUtc.isSame(todayStartUtc, 'day')
		) {
			return 'today';
		}
		if (
			startDayUtc.isSame(
				dayjs.utc().subtract(1, 'day').startOf('day'),
				'day'
			) &&
			endDayUtc.isSame(dayjs.utc().subtract(1, 'day').startOf('day'), 'day')
		) {
			return 'yesterday';
		}
		if (
			startDayUtc.isSame(dayjs.utc().startOf('week'), 'day') &&
			endDayUtc.isSame(dayjs.utc().endOf('week'), 'day')
		) {
			return 'this_week';
		}
		if (
			startDayUtc.isSame(dayjs.utc().startOf('month'), 'day') &&
			endDayUtc.isSame(dayjs.utc().endOf('month'), 'day')
		) {
			return 'this_month';
		}
		return 'custom';
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());

		const hasTimeParams = queryData.startTime || queryData.endTime;

		const initialFilters = {
			startTime:
				hasTimeParams &&
				queryData.startTime &&
				!Number.isNaN(Number(queryData.startTime))
					? queryData.startTime
					: '',
			endTime:
				hasTimeParams &&
				queryData.endTime &&
				!Number.isNaN(Number(queryData.endTime))
					? queryData.endTime
					: '',
			network: queryData.network || '',
			currency: queryData.currency || '',
			walletAddress: queryData.walletAddress || '',
		};

		setFilters(initialFilters);
		const detectedRange = getDateRangeFromStartEnd(
			initialFilters.startTime,
			initialFilters.endTime
		);
		setDateRange(hasTimeParams ? detectedRange || 'today' : 'all');

		if (hasTimeParams && detectedRange !== 'all') {
			const startUtc = dayjs.utc(Number(initialFilters.startTime));
			const endUtc = dayjs.utc(Number(initialFilters.endTime));
			if (startUtc.isValid()) {
				setStartTime(startUtc.format('HH:mm'));
			} else {
				setStartTime(null);
			}
			if (endUtc.isValid()) {
				setEndTime(endUtc.format('HH:mm'));
			} else {
				setEndTime(null);
			}
		} else {
			setStartTime(null);
			setEndTime(null);
		}

		if (detectedRange === 'custom' && hasTimeParams) {
			const startDay = dayjs
				.utc(Number(initialFilters.startTime))
				.startOf('day');
			const endDay = dayjs.utc(Number(initialFilters.endTime)).startOf('day');
			const diffDays = endDay.diff(startDay, 'day');
			if (diffDays > 90) {
				setDateRangeError('Date range cannot exceed 90 days.');
			} else if (endDay.isAfter(dayjs.utc().startOf('day'))) {
				setDateRangeError('End date cannot be after the current day.');
			} else {
				setDateRangeError(null);
			}
		} else {
			setDateRangeError(null);
		}

		const filterListNames = searchFilters
			.filter((x) => queryData[x.name])
			.map((y) => y.label);

		setActiveFilters([...new Set([...filterListNames, 'Date Range'])]);
	}, [searchParams]);

	const handleDateRangeChange = (value: string) => {
		setDateRange(value);
		setStartTime(null);
		setEndTime(null);
		setDateRangeError(null);

		const newFilters = { ...filters };

		switch (value) {
			case 'today':
				newFilters.startTime = dayjs.utc().startOf('day').valueOf().toString();
				newFilters.endTime = dayjs.utc().endOf('day').valueOf().toString();
				break;
			case 'yesterday':
				newFilters.startTime = dayjs
					.utc()
					.subtract(1, 'day')
					.startOf('day')
					.valueOf()
					.toString();
				newFilters.endTime = dayjs
					.utc()
					.subtract(1, 'day')
					.endOf('day')
					.valueOf()
					.toString();
				break;
			case 'this_week':
				newFilters.startTime = dayjs.utc().startOf('week').valueOf().toString();
				newFilters.endTime = dayjs.utc().endOf('week').valueOf().toString();
				break;
			case 'this_month':
				newFilters.startTime = dayjs
					.utc()
					.startOf('month')
					.valueOf()
					.toString();
				newFilters.endTime = dayjs.utc().endOf('month').valueOf().toString();
				break;
			case 'custom':
			case 'all':
				newFilters.startTime = '';
				newFilters.endTime = '';
				break;
			default:
				break;
		}

		setFilters(newFilters);
	};

	const handleTimeRangeChange = useCallback(
		(time: string | null, type: 'start' | 'end') => {
			if (type === 'start') {
				setStartTime(time);
			} else {
				setEndTime(time);
			}

			setFilters((prevFilters) => {
				const newFilters = { ...prevFilters };
				const baseDate =
					prevFilters.startTime && !Number.isNaN(Number(prevFilters.startTime))
						? dayjs.utc(Number(prevFilters.startTime)).startOf('day')
						: dayjs.utc().startOf('day');

				if (time && dayjs(time, 'HH:mm', true).isValid()) {
					const timeParsed = dayjs(time, 'HH:mm', true);
					const hours = timeParsed.hour();
					const minutes = timeParsed.minute();

					if (type === 'start') {
						const startTimeParsed = baseDate
							.hour(hours)
							.minute(minutes)
							.second(0)
							.millisecond(0);
						newFilters.startTime = startTimeParsed.valueOf().toString();
					} else {
						let endTimeParsed = baseDate
							.hour(hours)
							.minute(minutes)
							.second(59)
							.millisecond(999);
						const startTimeParsed = prevFilters.startTime
							? dayjs.utc(Number(prevFilters.startTime))
							: baseDate;
						if (endTimeParsed.isBefore(startTimeParsed)) {
							endTimeParsed = endTimeParsed.add(1, 'day');
						}
						newFilters.endTime = endTimeParsed.valueOf().toString();
					}
				} else if (!time) {
					if (type === 'start') {
						newFilters.startTime = baseDate.valueOf().toString();
					} else {
						newFilters.endTime = baseDate.endOf('day').valueOf().toString();
					}
				}

				return newFilters;
			});
		},
		[]
	);

	const submitFilter = () => {
		const queryData = Object.fromEntries(searchParams.entries());
		const merchantId = queryData.merchantId || '';

		if (dateRange === 'custom' && (!filters.startTime || !filters.endTime)) {
			return;
		}

		let finalFilters = { ...filters };

		if (dateRange === 'custom' && (startTime || endTime)) {
			const baseDate =
				filters.startTime && !Number.isNaN(Number(filters.startTime))
					? dayjs.utc(Number(filters.startTime)).startOf('day')
					: dayjs.utc().startOf('day');

			const startTimeParsed =
				startTime && dayjs(startTime, 'HH:mm', true).isValid()
					? baseDate
							.hour(dayjs(startTime, 'HH:mm', true).hour())
							.minute(dayjs(startTime, 'HH:mm', true).minute())
							.second(0)
							.millisecond(0)
					: baseDate;

			let endTimeParsed =
				endTime && dayjs(endTime, 'HH:mm', true).isValid()
					? baseDate
							.hour(dayjs(endTime, 'HH:mm', true).hour())
							.minute(dayjs(endTime, 'HH:mm', true).minute())
							.second(59)
							.millisecond(999)
					: baseDate.endOf('day');

			if (endTimeParsed.isBefore(startTimeParsed)) {
				endTimeParsed = endTimeParsed.add(1, 'day');
			}

			finalFilters = {
				...filters,
				startTime: startTimeParsed.valueOf().toString(),
				endTime: endTimeParsed.valueOf().toString(),
			};
		}

		if (dateRange === 'all') {
			delete finalFilters.startTime;
			delete finalFilters.endTime;
		}

		const updatedParams = {
			...finalFilters,
			pageId: '1',
			...(merchantId && { merchantId }),
		};

		retrieveQueryString(updatedParams);
		setSearchParams(updatedParams);
		setFilters(finalFilters);

		const filterListNames = searchFilters
			.filter((x) => updatedParams[x.name])
			.map((y) => y.label);
		setActiveFilters([...new Set([...filterListNames, 'Date Range'])]);
	};

	const removeAFilter = (filterLabel: string) => {
		const queryData = Object.fromEntries(searchParams.entries());
		const merchantId = queryData.merchantId || '';
		const filterOptionItems = searchFilters.filter(
			(x) => x.label === filterLabel
		);

		const newFilters = { ...filters };
		filterOptionItems.forEach((item) => {
			if (item.label === 'Date Range') {
				delete queryData.startTime;
				delete queryData.endTime;
				newFilters.startTime = dayjs.utc().startOf('day').valueOf().toString();
				newFilters.endTime = dayjs.utc().endOf('day').valueOf().toString();
				setDateRange('today');
				setStartTime(null);
				setEndTime(null);
				setDateRangeError(null);
			} else {
				delete queryData[item.name];
				newFilters[item.name] = '';
			}
		});

		const cleanedQueryData = Object.fromEntries(
			Object.entries(queryData).filter(([_, value]) => value)
		);

		const updatedParams = {
			...cleanedQueryData,
			pageId: '1',
			...(merchantId && { merchantId }),
		};
		retrieveQueryString(updatedParams);
		setSearchParams(updatedParams);
		setFilters(newFilters);

		const filterListNames = searchFilters
			.filter((x) => cleanedQueryData[x.name])
			.map((y) => y.label);
		setActiveFilters([...new Set([...filterListNames, 'Date Range'])]);
	};

	const disabledDate = (current: dayjs.Dayjs) => {
		if (!current) return false;
		const candidateUtcStart = dayjs
			.utc(current.format('YYYY-MM-DD'))
			.startOf('day');
		return candidateUtcStart.isAfter(dayjs.utc().startOf('day'));
	};

	return (
		<Card title="Filters" style={{ marginBottom: 24 }}>
			<Form layout="vertical">
				<Row gutter={[16, 16]}>
					<Col xs={24} sm={12} md={8} lg={4}>
						<Form.Item label="Date Range">
							<Select value={dateRange} onChange={handleDateRangeChange}>
								<Select.Option value="all">All</Select.Option>
								<Select.Option value="today">Today</Select.Option>
								<Select.Option value="yesterday">Yesterday</Select.Option>
								<Select.Option value="this_week">This Week</Select.Option>
								<Select.Option value="this_month">This Month</Select.Option>
								<Select.Option value="custom">Custom Range</Select.Option>
							</Select>
						</Form.Item>
					</Col>

					{dateRange === 'custom' && (
						<Col xs={24} sm={24} md={12} lg={8}>
							<Form.Item
								label="Custom Date Range (UTC)"
								validateStatus={dateRangeError || error ? 'error' : undefined}
								help={dateRangeError || error}
							>
								<DateRangePickerUTC
									startTime={filters.startTime}
									endTime={filters.endTime}
									onChange={(startUtc, endUtc) => {
										setFilters({
											...filters,
											startTime: startUtc,
											endTime: endUtc,
										});
									}}
									onError={setError}
								/>
							</Form.Item>
						</Col>
					)}

					{dateRange !== 'all' && (
						<>
							<Col xs={24} sm={12} md={8} lg={4}>
								<Form.Item label="Start Time (UTC)">
									<input
										type="time"
										value={startTime || ''}
										onChange={(e) =>
											handleTimeRangeChange(e.target.value || null, 'start')
										}
										style={{
											width: '100%',
											minWidth: '120px',
											maxWidth: '150px',
											padding: '4px 11px',
											border: '1px solid #d9d9d9',
											borderRadius: '2px',
											fontSize: '14px',
											lineHeight: '1.5715',
										}}
									/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={12} md={8} lg={4}>
								<Form.Item label="End Time (UTC)">
									<input
										type="time"
										value={endTime || ''}
										onChange={(e) =>
											handleTimeRangeChange(e.target.value || null, 'end')
										}
										style={{
											width: '100%',
											minWidth: '120px',
											maxWidth: '150px',
											padding: '4px 11px',
											border: '1px solid #d9d9d9',
											borderRadius: '2px',
											fontSize: '14px',
											lineHeight: '1.5715',
										}}
									/>
								</Form.Item>
							</Col>
						</>
					)}

					<Col xs={24} sm={12} md={8} lg={4}>
						<Form.Item label="Blockchain">
							<Select
								allowClear
								placeholder="Select Blockchain"
								value={filters.network || undefined}
								onChange={(val) =>
									setFilters({ ...filters, network: val || '' })
								}
							>
								{blockchainSelect.map((optionItem) => (
									<Select.Option
										key={optionItem.value}
										value={optionItem.value}
									>
										{optionItem.name}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>

					<Col xs={24} sm={12} md={8} lg={4}>
						<Form.Item label="Currency">
							<Select
								allowClear
								placeholder="Select Currency"
								value={filters.currency || undefined}
								onChange={(val) =>
									setFilters({ ...filters, currency: val || '' })
								}
							>
								{currencySelect.map((optionItem) => (
									<Select.Option
										key={optionItem.value}
										value={optionItem.value}
									>
										{optionItem.name}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>

					<Col xs={24} sm={12} md={8} lg={4}>
						<Form.Item label="Search Wallet">
							<Input
								placeholder="Enter wallet address"
								value={filters.walletAddress}
								onChange={(e) =>
									setFilters({ ...filters, walletAddress: e.target.value })
								}
							/>
						</Form.Item>
					</Col>

					<div style={{ display: 'flex', alignItems: 'center' }}>
						<Button
							onClick={submitFilter}
							type={
								(dateRange === 'custom' &&
									filters.startTime &&
									filters.endTime &&
									!dateRangeError) ||
								(dateRange !== 'custom' &&
									Object.values(filters).some((v) => v))
									? 'primary'
									: 'default'
							}
							disabled={
								(dateRange === 'custom' &&
									(!filters.startTime || !filters.endTime || dateRangeError)) ||
								false
							}
						>
							Filter
						</Button>
					</div>
				</Row>
			</Form>
			<div style={{ display: 'flex', alignItems: 'center', marginTop: 16 }}>
				<span className="material-symbols-outlined" style={{ fontSize: 21 }}>
					filter_list
				</span>
				<span style={{ marginRight: 7 }}>Filters </span>
				{activeFilters.map((item) => (
					<Tag
						key={item}
						color="blue"
						closable
						onClose={() => removeAFilter(item)}
					>
						{item}
					</Tag>
				))}
			</div>
		</Card>
	);
};

export default FilterRemittanceLogs;
